import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import FilterPanel from '../components/FilterPanel';
import FilterPanelAccordion from '../components/FilterPanelAccordion';
import FilterPillStrip, { countActiveFilters } from '../components/FilterPillStrip';
import FilterSheet from '../components/FilterSheet';
import ParcelSummarySheet from '../components/ParcelSummarySheet';
import MapView from '../components/MapView';
import MapListPanel from '../components/MapListPanel';
import useBreakpoint from '../hooks/useBreakpoint';
import {
  listBookmarks,
  getDownvotes,
  getViewedIds,
  createBookmark,
  deleteBookmark,
  getParcelsTable,
} from '../services/api';
import { isApnLikeQuery } from '../utils/apnQuery';

// View mode toggle: map-only (existing behavior, the default),
// split (map + list panel), or list-only (panel takes full width).
// Persisted to localStorage so the user's preference survives refresh.
const VIEW_MODE_KEY = 'landai_search_view_mode';
const VALID_MODES = new Set(['map', 'split', 'list']);
function loadViewMode() {
  try {
    const v = localStorage.getItem(VIEW_MODE_KEY);
    if (VALID_MODES.has(v)) return v;
  } catch { /* ignore */ }
  return 'map';
}

function useIsMobile(breakpoint = 1024) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= breakpoint);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [breakpoint]);
  return isMobile;
}

export const STORAGE_KEY = 'landai_map_state';

// Exported for unit testing. Reads the saved map state (filters +
// center + zoom) from sessionStorage. Returns null on missing data
// or any parse/access error so the caller can use defaults.
export function loadSavedState() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return null;
}

// Exported for unit testing. Serializes and persists the current
// map state to sessionStorage. Silently swallows quota-exceeded
// and security errors so a full sessionStorage (rare) doesn't
// crash the search page.
export function saveState(state) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { /* ignore */ }
}

// Valid sort keys for the list panel — mirrors MapListPanel SORT_OPTIONS.
const VALID_SORTS = new Set([
  'opportunity_score:desc', 'listing_price:asc', 'listing_price:desc',
  'area_acres:desc', 'area_acres:asc', 'days_on_market:asc',
]);

// Valid generation technologies for the #997 proximity/tech filter.
// Matches the backend `generation_tech` param allowlist (parcels.py) and
// the /generation/points endpoint's VALID_TECHS.
const VALID_GENERATION_TECHS = new Set(['wind', 'solar', 'geothermal']);

// Valid HIFLD voltage classes + line types for the #991 transmission-line
// proximity filter. Matches the backend VOLT_CLASS_STYLE keys
// (transmission_lines.py) and the AC/DC line-type contract. Anything off
// the allowlist → null (no filter).
const VALID_POWERLINE_VOLT_CLASSES = new Set([
  'UNDER 100', '100-161', '220-287', '345', '500', '735 AND ABOVE', 'DC',
]);
const VALID_POWERLINE_LINE_TYPES = new Set(['AC', 'DC']);

// Days-on-market bounds for the list panel, kept in parity with MapView
// (#1063). The "Days on Market" dropdown writes `filters.domFilter` as
// `<N` (→ max_dom) or `>N` (→ min_dom); MapView parses it but MapListPanel
// was reading `filters.minDom`/`filters.maxDom` — keys only ever set by the
// "Motivated seller" quick-screen — so the dropdown never filtered the list.
// Derive from both so map and list counts agree.
export function deriveDomBounds(filters) {
  const dom = typeof filters.domFilter === 'string' ? filters.domFilter : '';
  let minDom;
  let maxDom;
  if (dom.startsWith('>')) {
    const n = parseInt(dom.slice(1), 10);
    if (Number.isFinite(n)) minDom = n;
  } else if (dom.startsWith('<')) {
    const n = parseInt(dom.slice(1), 10);
    if (Number.isFinite(n)) maxDom = n;
  }
  // Fall back to the quick-screen keys when the dropdown didn't set a bound.
  if (minDom == null) {
    const n = parseInt(filters.minDom, 10);
    if (Number.isFinite(n)) minDom = n;
  }
  if (maxDom == null) {
    const n = parseInt(filters.maxDom, 10);
    if (Number.isFinite(n)) maxDom = n;
  }
  return { minDom, maxDom };
}

// Parse URL query params into the shape SearchPage understands.
// URL params win over sessionStorage when present.
// Shape: ?c=lat,lon&z=zoom&st=state&v=view_mode&ls=sort:order
//        &mn_ac=N&mx_ac=N&mn_pr=N&mx_pr=N
export function parseUrlParams(searchParams) {
  const c = searchParams.get('c');
  const z = searchParams.get('z');
  const st = searchParams.get('st');
  const il = searchParams.get('il');
  const v = searchParams.get('v');
  const ls = searchParams.get('ls');
  const mnAc = searchParams.get('mn_ac');
  const mxAc = searchParams.get('mx_ac');
  const mnPr = searchParams.get('mn_pr');
  const mxPr = searchParams.get('mx_pr');
  // `fp` (focus-parcel) — set by ParcelDetail's Locate button so the
  // map zooms to this parcel's actual boundary via FocusFlyer (#708).
  // Without it, navigating from the detail page just dropped the camera
  // at the centroid with z=14; if the user was already on the map at
  // the same area the camera didn't visibly move.
  const fp = searchParams.get('fp');
  // Investment-thesis tag filter (#771 / INIT-thesis-tagger). One tag
  // per URL — the chip strip is multi-select in UI but the backend
  // filter is single-tag; multi-select drives client-side composition
  // (intersection) once we have a use case for it. URL key matches the
  // backend query param (`thesis_tag`) so deep-linking from
  // ParcelDetail chips works directly.
  const thesisTag = searchParams.get('thesis_tag');
  // Climate-risk consolidator (#773 / INIT-climate-risk). URL key
  // matches the backend query param so a shared link applies the
  // same cap on the recipient's view.
  const maxClimateRiskRaw = searchParams.get('max_climate_risk');
  const maxClimateRisk = maxClimateRiskRaw != null
    ? Math.max(0, Math.min(100, parseInt(maxClimateRiskRaw, 10)))
    : null;
  // Listing-quality predictor floor (#775 / INIT-listing-quality).
  // Symmetric handling with maxClimateRisk — URL key matches the
  // backend query param. NULL when the slider is at 0 ("Any").
  const minListingQualityRaw = searchParams.get('min_listing_quality');
  const minListingQuality = minListingQualityRaw != null
    ? Math.max(0, Math.min(100, parseInt(minListingQualityRaw, 10)))
    : null;
  // Sale-probability filter floor (#777 / INIT-sale-probability). The
  // UI slider is on the 60d horizon — same key as the backend param.
  // Stored as an integer percent (0-100) in the URL but the backend
  // expects 0-1 float; SearchPage converts on the way out.
  const minSaleProbability60dRaw = searchParams.get('min_sale_probability_60d');
  const minSaleProbability60d = minSaleProbability60dRaw != null
    ? Math.max(0, Math.min(100, parseInt(minSaleProbability60dRaw, 10)))
    : null;
  // Fair-value max-percent-above filter (#779 / INIT-fair-value). The
  // UI slider is an integer percent in [-50, 500] — same key as the
  // backend param. Backend expects a plain number (not 0-1 fraction)
  // so SearchPage forwards the URL value as-is.
  const maxPctAboveFairValueRaw = searchParams.get('max_pct_above_fair_value');
  const maxPctAboveFairValue = maxPctAboveFairValueRaw != null
    ? Math.max(-50, Math.min(500, parseInt(maxPctAboveFairValueRaw, 10)))
    : null;
  // Investment-yield floor (#785 / INIT-yield-calc). URL key matches the
  // backend query param; slider grain is an integer percent in [0, 30].
  // NULL when slider sits at 0 ("Any") so a shared link doesn't carry
  // a pointless `?min_expected_yield=0`.
  const minExpectedYieldRaw = searchParams.get('min_expected_yield');
  const minExpectedYield = minExpectedYieldRaw != null
    ? Math.max(0, Math.min(30, parseInt(minExpectedYieldRaw, 10)))
    : null;
  // Description-NER booleans (#781 / INIT-description-ner). Each is a
  // tri-state filter persisted as `?<key>=1` when on, omitted when off
  // (so the URL stays clean). The keys match the backend query params
  // so deep-linking from a chip on ParcelDetail works directly. Anything
  // other than the literal "1" parses to null (no filter).
  const gatedCommunityOnly = searchParams.get('gated_community_only') === '1' ? true : null;
  const noHoaOnly = searchParams.get('no_hoa_only') === '1' ? true : null;
  const subdividableOnly = searchParams.get('subdividable_only') === '1' ? true : null;
  // Photo-content classifier booleans (#783 / INIT-photo-content). Same
  // tri-state shape as the description-NER toggles above: present-and-`1`
  // means TRUE, anything else parses to null (so a typo or legacy URL
  // doesn't accidentally enable a filter the user didn't ask for).
  const hasHousePhotoOnly = searchParams.get('has_house_photo_only') === '1' ? true : null;
  const hasWaterPhotoOnly = searchParams.get('has_water_photo_only') === '1' ? true : null;
  const hasLongViewPhotoOnly = searchParams.get('has_long_view_photo_only') === '1' ? true : null;
  // OPP-3 stable-ground filter (#221 / REQ-005). URL key matches the
  // backend query param. Tri-state: present+`=1` → true, anything else →
  // null (no filter). Paired with FilterSheet chip + handleStableGroundToggle.
  const stableGroundOnly = searchParams.get('stable_ground') === '1' ? true : null;
  // Alt-energy generation-site proximity (#997). URL key matches the
  // backend `/parcels` query param `max_dist_generation_km` (float km).
  // NULL / non-numeric / negative → no filter. `generation_tech`
  // restricts the proximity check to a single technology; only the three
  // valid techs pass, everything else → null (no filter).
  const maxGenerationDistRaw = searchParams.get('max_dist_generation_km');
  const maxGenerationDistNum = maxGenerationDistRaw != null ? parseFloat(maxGenerationDistRaw) : NaN;
  const maxGenerationDist = Number.isFinite(maxGenerationDistNum) && maxGenerationDistNum >= 0
    ? maxGenerationDistNum
    : null;
  const generationTechRaw = searchParams.get('generation_tech');
  const generationTech = VALID_GENERATION_TECHS.has(generationTechRaw)
    ? generationTechRaw
    : null;
  // HIFLD transmission-line proximity (#991). Distance is km in the URL
  // (converted to metres at the API boundary since the backend param is
  // metres). NULL / non-numeric / negative → no filter. voltage is a
  // positive kV float; volt class + line type come from allowlists.
  const maxPowerlineDistRaw = searchParams.get('max_powerline_dist_km');
  const maxPowerlineDistNum = maxPowerlineDistRaw != null ? parseFloat(maxPowerlineDistRaw) : NaN;
  const maxPowerlineDist = Number.isFinite(maxPowerlineDistNum) && maxPowerlineDistNum >= 0
    ? maxPowerlineDistNum
    : null;
  const minPowerlineVoltageRaw = searchParams.get('min_powerline_voltage');
  const minPowerlineVoltageNum = minPowerlineVoltageRaw != null ? parseFloat(minPowerlineVoltageRaw) : NaN;
  const minPowerlineVoltage = Number.isFinite(minPowerlineVoltageNum) && minPowerlineVoltageNum >= 0
    ? minPowerlineVoltageNum
    : null;
  const powerlineVoltClassRaw = searchParams.get('powerline_volt_class');
  const powerlineVoltClass = VALID_POWERLINE_VOLT_CLASSES.has(powerlineVoltClassRaw)
    ? powerlineVoltClassRaw
    : null;
  const powerlineLineTypeRaw = searchParams.get('powerline_line_type');
  const powerlineLineType = VALID_POWERLINE_LINE_TYPES.has(powerlineLineTypeRaw)
    ? powerlineLineTypeRaw
    : null;
  return {
    center: (() => {
      if (!c) return null;
      const parts = c.split(',').map(Number);
      return parts.length === 2 && parts.every(n => !isNaN(n)) ? parts : null;
    })(),
    zoom: z ? parseInt(z, 10) : null,
    state: st || null,
    isListed: il === '1' ? true : il === '0' ? false : null,
    viewMode: VALID_MODES.has(v) ? v : null,
    listSort: VALID_SORTS.has(ls) ? ls : null,
    minArea: mnAc ? mnAc : null,
    maxArea: mxAc ? mxAc : null,
    minPrice: mnPr ? mnPr : null,
    maxPrice: mxPr ? mxPr : null,
    focusParcelId: fp || null,
    thesisTag: thesisTag || null,
    maxClimateRisk: Number.isFinite(maxClimateRisk) ? maxClimateRisk : null,
    minListingQuality: Number.isFinite(minListingQuality) ? minListingQuality : null,
    minSaleProbability60d: Number.isFinite(minSaleProbability60d) ? minSaleProbability60d : null,
    maxPctAboveFairValue: Number.isFinite(maxPctAboveFairValue) ? maxPctAboveFairValue : null,
    minExpectedYield: Number.isFinite(minExpectedYield) ? minExpectedYield : null,
    gatedCommunityOnly,
    noHoaOnly,
    subdividableOnly,
    hasHousePhotoOnly,
    hasWaterPhotoOnly,
    hasLongViewPhotoOnly,
    stableGroundOnly,
    // #997 alt-energy generation-site proximity + tech.
    maxGenerationDist,
    generationTech,
    // #991 HIFLD transmission-line proximity (distance km + voltage/class/type).
    maxPowerlineDist,
    minPowerlineVoltage,
    powerlineVoltClass,
    powerlineLineType,
  };
}

function SearchPage() {
  const [searchParams] = useSearchParams();
  const urlState = useRef(parseUrlParams(searchParams));
  const saved = useRef(loadSavedState());
  const urlSyncTimer = useRef(null);
  // `useIsMobile` (≤1024) drives the legacy mobile filter-toggle button and
  // the 2-option view-mode toggle. Tablet+desktop behavior is owned by this
  // flag and stays unchanged from PR-2.
  const isMobile = useIsMobile();
  // `useBreakpoint()` is the PR-1 hook returning 'phone' | 'tablet' | 'desktop'.
  // Phone branch (≤767) gets PR-3's pill-strip / FilterSheet / ParcelSummarySheet;
  // tablet (768-1024) still falls through `isMobile` paths but keeps the
  // FilterPanel sidebar visible (no PR-3 changes).
  const breakpoint = useBreakpoint();
  const isPhone = breakpoint === 'phone';
  // PR-5 (#704) — tablet (768-1024) gets the accordion-wrapped FilterPanel
  // so the long flat sidebar matches the phone FilterSheet's 4-group
  // mental model.
  // PR-6 (#704) — desktop (≥1025) also gets the accordion-wrapped
  // FilterPanel so the three breakpoints share one mental model for
  // filter grouping. Section open/closed state persists in
  // localStorage per the FilterPanelAccordion implementation.
  const isTablet = breakpoint === 'tablet';
  const isDesktop = breakpoint === 'desktop';
  const useAccordionPanel = isTablet || isDesktop;

  const [filters, setFilters] = useState(() => {
    const base = saved.current?.filters || {};
    if (urlState.current.state) base.state = urlState.current.state;
    if (urlState.current.isListed !== null) base.isListed = urlState.current.isListed;
    if (urlState.current.minArea) base.minArea = urlState.current.minArea;
    if (urlState.current.maxArea) base.maxArea = urlState.current.maxArea;
    if (urlState.current.minPrice) base.minPrice = urlState.current.minPrice;
    if (urlState.current.maxPrice) base.maxPrice = urlState.current.maxPrice;
    if (urlState.current.thesisTag) base.thesisTag = urlState.current.thesisTag;
    // #773 (INIT-climate-risk): hydrate maxClimateRisk from URL so a
    // shared link reproduces the slider position.
    if (urlState.current.maxClimateRisk != null) {
      base.maxClimateRisk = urlState.current.maxClimateRisk;
    }
    // #775 (INIT-listing-quality): symmetric hydrate for the listing-
    // quality floor slider.
    if (urlState.current.minListingQuality != null) {
      base.minListingQuality = urlState.current.minListingQuality;
    }
    // #777 (INIT-sale-probability): hydrate the 60-day sale probability
    // floor slider so a shared link reproduces the same view.
    if (urlState.current.minSaleProbability60d != null) {
      base.minSaleProbability60d = urlState.current.minSaleProbability60d;
    }
    // #779 (INIT-fair-value): hydrate the "max % above fair value"
    // slider from the URL. Persists alongside the other filter
    // sliders so a shared "underpriced listings only" link reproduces
    // the same scope.
    if (urlState.current.maxPctAboveFairValue != null) {
      base.maxPctAboveFairValue = urlState.current.maxPctAboveFairValue;
    }
    // #785 (INIT-yield-calc): hydrate the min expected 5y yield slider
    // from the URL so a shared link reproduces the same scope.
    if (urlState.current.minExpectedYield != null) {
      base.minExpectedYield = urlState.current.minExpectedYield;
    }
    // #781 (INIT-description-ner): hydrate the three description-NER
    // toggles so a shared "gated community + no HOA" link reproduces
    // the same scoped result set on the recipient's screen.
    if (urlState.current.gatedCommunityOnly) base.gatedCommunityOnly = true;
    if (urlState.current.noHoaOnly) base.noHoaOnly = true;
    if (urlState.current.subdividableOnly) base.subdividableOnly = true;
    // #783 (INIT-photo-content): hydrate the three photo-content toggles
    // so a shared "has water + has long-view" link reproduces the same
    // scoped result set on the recipient's screen.
    if (urlState.current.hasHousePhotoOnly) base.hasHousePhotoOnly = true;
    if (urlState.current.hasWaterPhotoOnly) base.hasWaterPhotoOnly = true;
    if (urlState.current.hasLongViewPhotoOnly) base.hasLongViewPhotoOnly = true;
    // OPP-3 stable-ground filter (REQ-005): hydrate from URL so a shared
    // "stable ground only" link reproduces the same scoped result set.
    if (urlState.current.stableGroundOnly) base.stableGroundOnly = true;
    // #997 alt-energy generation-site proximity + tech: hydrate from URL
    // so a shared "within 10 km of wind" link reproduces the same scope.
    if (urlState.current.maxGenerationDist != null) {
      base.maxGenerationDist = urlState.current.maxGenerationDist;
    }
    if (urlState.current.generationTech) base.generationTech = urlState.current.generationTech;
    // #991 transmission-line proximity: hydrate from URL so a shared
    // "within 5 km of a 500 kV line" link reproduces the same scope.
    if (urlState.current.maxPowerlineDist != null) {
      base.maxPowerlineDist = urlState.current.maxPowerlineDist;
    }
    if (urlState.current.minPowerlineVoltage != null) {
      base.minPowerlineVoltage = urlState.current.minPowerlineVoltage;
    }
    if (urlState.current.powerlineVoltClass) base.powerlineVoltClass = urlState.current.powerlineVoltClass;
    if (urlState.current.powerlineLineType) base.powerlineLineType = urlState.current.powerlineLineType;
    return base;
  });
  const [selectedParcel, setSelectedParcel] = useState(null);
  const [mapData, setMapData] = useState({ count: 0, limit: 5000, points: [] });
  const [loading, setLoading] = useState(false);
  const [mapView, setMapView] = useState({
    center: (urlState.current.center?.length === 2 ? urlState.current.center : null)
      ?? saved.current?.center
      ?? [39.7392, -104.9903],
    zoom: urlState.current.zoom ?? saved.current?.zoom ?? 5,
  });
  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());
  // Downvoted (hidden) parcels — issue #124. Map filters them out by
  // default; when `filters.showHidden` is true MapView renders them
  // in red instead. Loaded on mount and refreshed on window focus,
  // identical lifecycle to bookmarkedIds so navigating back from
  // ParcelDetail picks up new hides immediately.
  const [downvotedIds, setDownvotedIds] = useState(new Set());
  // Viewed parcel IDs (#183) — used by PropertyCard to show a "Viewed"
  // badge and by the "Not Viewed" filter to hide seen parcels.
  const [viewedIds, setViewedIds] = useState(new Set());
  const [filtersOpen, setFiltersOpen] = useState(false);
  // PR-3 phone state ---------------------------------------------------
  // FilterSheet (full-screen modal) open/close, separate from the legacy
  // tablet `filtersOpen` toggle so the two don't collide if a user
  // crosses the 768px boundary mid-session.
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  // Parcel selected by tapping a phone-map marker. When non-null,
  // ParcelSummarySheet slides up; clearing it dismisses the sheet.
  // Distinct from `selectedParcel` (used by MapView's popup wiring) so
  // we don't accidentally retrigger the sheet from desktop popup clicks.
  const [summaryParcel, setSummaryParcel] = useState(null);
  // Save-toggle pending flag for the summary sheet's Save button so the
  // user can't double-tap during an in-flight create/delete bookmark API call.
  const [summarySaving, setSummarySaving] = useState(false);

  // Split-view state (#146 slice 1). Map's current bbox is propagated
  // from MapView via onBoundsChange and fed to MapListPanel so the
  // right-panel list always shows parcels in the current viewport.
  const [viewMode, setViewMode] = useState(
    () => urlState.current.viewMode || loadViewMode()
  );
  // List panel sort — lifted from MapListPanel so it can be encoded
  // in the URL for shareability. Shape: "sort_field:order" string.
  const [listSort, setListSort] = useState(
    () => urlState.current.listSort || 'opportunity_score:desc'
  );
  const [bbox, setBbox] = useState(null);
  // Bidirectional sync (#146 slice 2): row click in the panel sets
  // focusedParcel → MapView flies to it + draws a sticky highlight
  // ring. Row hover sets hoveredParcelId → MapView flashes the
  // matching point. Hover state lives only while the cursor is over
  // a row (cleared on mouseleave); focus persists until the user
  // clicks a different row or pans away from it.
  // #708: when ParcelDetail's Locate button navigates here with `?fp=<id>`,
  // seed focusedParcel with that ID so FocusFlyer fetches the parcel
  // polygon on mount and fitBounds to the actual boundary. lat/lon are
  // best-effort from the URL `c=...` param; FocusFlyer falls back to
  // its own centroid fly-to if geometry can't be fetched.
  const initialFocusedParcel = (() => {
    const fp = urlState.current.focusParcelId;
    if (!fp) return null;
    const c = urlState.current.center;
    return {
      parcel_id: fp,
      lat: c && c.length === 2 ? c[0] : null,
      lon: c && c.length === 2 ? c[1] : null,
    };
  })();
  const [focusedParcel, setFocusedParcel] = useState(initialFocusedParcel);
  const [hoveredParcelId, setHoveredParcelId] = useState(null);

  // #1176: APN/parcel-number search → zoom to the parcel boundary.
  // Owner searched "0594-271-49-0000"; the q-search FOUND the parcel
  // (parcel_id limb, #1205) but nothing moved the camera: the only
  // q-driven camera path was the Nominatim place geocode, which can't
  // resolve an APN, so "it didn't seem like it found it". When the
  // query looks like an APN (digits+dashes — mirrors the backend's
  // _APN_LIKE_RE) and resolves to EXACTLY ONE parcel, seed
  // focusedParcel so MapView's FocusFlyer (#708) fetches the polygon
  // and fitBounds to it (centroid flyTo fallback inside FocusFlyer).
  // Multi-match queries keep the current behavior (dots + list show
  // the candidates; the user picks one via "Locate").
  // Debounced 600ms — same grain as the geocode — so partial APNs
  // typed en route don't yank the camera to the wrong parcel.
  useEffect(() => {
    const q = (filters.q || '').trim();
    if (!isApnLikeQuery(q)) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        // per_page=2 so `parcels.length` alone could disambiguate, but
        // `total` is authoritative for uniqueness.
        const data = await getParcelsTable({ q, page: 1, per_page: 2 });
        if (cancelled) return;
        const parcels = data?.parcels || [];
        if (data?.total === 1 && parcels.length === 1) {
          const p = parcels[0];
          // Fresh object per trigger — FocusFlyer keys on the object
          // reference so a re-search of the same APN re-zooms.
          setFocusedParcel({
            parcel_id: p.parcel_id,
            lat: p.latitude ?? p.centroid?.lat ?? null,
            lon: p.longitude ?? p.centroid?.lon ?? null,
          });
        }
      } catch (err) {
        // Non-fatal: the list/dot surfaces still show the result. Leave
        // a trace instead of failing silently (#1176).
        console.warn('APN search auto-focus failed:', err);
      }
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [filters.q]);
  useEffect(() => {
    try { localStorage.setItem(VIEW_MODE_KEY, viewMode); } catch { /* ignore */ }
  }, [viewMode]);

  // Load bookmarked + downvoted parcel IDs on mount, and refresh
  // when the tab regains focus. This keeps the bookmark / hide
  // state on the map fresh after the user changes it on the detail
  // page or from another tab.
  useEffect(() => {
    let cancelled = false;
    const fetchSavedSets = async () => {
      try {
        // Fire all three fetches in parallel — they touch different
        // tables and none blocks the others. Promise.allSettled
        // so a failure on one doesn't wipe the other's state.
        const [bmRes, dvRes, vwRes] = await Promise.allSettled([
          listBookmarks(),
          getDownvotes(),
          getViewedIds(),
        ]);
        if (cancelled) return;
        if (bmRes.status === 'fulfilled') {
          setBookmarkedIds(new Set((bmRes.value || []).map(p => p.parcel_id)));
        } else {
          console.error('Failed to load bookmarks:', bmRes.reason);
        }
        if (dvRes.status === 'fulfilled') {
          setDownvotedIds(new Set((dvRes.value || []).map(p => p.parcel_id)));
        } else {
          console.error('Failed to load downvotes:', dvRes.reason);
        }
        if (vwRes.status === 'fulfilled') {
          setViewedIds(new Set((vwRes.value || []).map(p => p.parcel_id)));
        }
      } catch (err) {
        console.error('Failed to load saved sets:', err);
      }
    };
    fetchSavedSets();

    // Refresh both sets when the window gets focus — e.g. after the
    // user bookmarks/hides a parcel on the detail page and
    // navigates back via the browser back button, or after a
    // change in another tab.
    const onFocus = () => { fetchSavedSets(); };
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  // Persist filters + map position to sessionStorage
  useEffect(() => {
    saveState({
      filters,
      center: mapView.center,
      zoom: mapView.zoom,
    });
  }, [filters, mapView]);

  // Keep URL in sync (debounced) so the current view is always shareable.
  // Encodes: center, zoom, state, viewMode, listSort, and price/acre ranges.
  useEffect(() => {
    if (urlSyncTimer.current) clearTimeout(urlSyncTimer.current);
    urlSyncTimer.current = setTimeout(() => {
      const params = {};
      const [lat, lon] = mapView.center;
      params.c = `${lat.toFixed(4)},${lon.toFixed(4)}`;
      params.z = String(mapView.zoom);
      if (filters.state) params.st = filters.state;
      if (viewMode !== 'map') params.v = viewMode;
      if (listSort !== 'opportunity_score:desc') params.ls = listSort;
      if (filters.minArea) params.mn_ac = filters.minArea;
      if (filters.maxArea) params.mx_ac = filters.maxArea;
      if (filters.minPrice) params.mn_pr = filters.minPrice;
      if (filters.maxPrice) params.mx_pr = filters.maxPrice;
      if (filters.thesisTag) params.thesis_tag = filters.thesisTag;
      // #773 (INIT-climate-risk): encode the slider position so it's
      // shareable. Empty string is the "no filter" state.
      if (filters.maxClimateRisk != null && filters.maxClimateRisk !== '') {
        params.max_climate_risk = String(filters.maxClimateRisk);
      }
      // #775 (INIT-listing-quality): encode the listing-quality floor.
      if (filters.minListingQuality != null && filters.minListingQuality !== '') {
        params.min_listing_quality = String(filters.minListingQuality);
      }
      // #777 (INIT-sale-probability): encode the 60-day sale-probability
      // floor as an integer percent (0-100) — the backend expects 0-1 so
      // SearchPage downconverts when emitting the API param via
      // FilterPanel → MapView → /parcels.
      if (filters.minSaleProbability60d != null && filters.minSaleProbability60d !== '') {
        params.min_sale_probability_60d = String(filters.minSaleProbability60d);
      }
      // #779 (INIT-fair-value): encode the "max % above fair value"
      // slider so an "underpriced listings only" link reproduces. The
      // backend takes the raw percent (no downconversion needed).
      if (filters.maxPctAboveFairValue != null && filters.maxPctAboveFairValue !== '') {
        params.max_pct_above_fair_value = String(filters.maxPctAboveFairValue);
      }
      // #785 (INIT-yield-calc): encode the min expected 5y yield
      // floor as an integer percent (0-30). The backend takes the raw
      // percent, same shape as the listing-quality slider.
      if (filters.minExpectedYield != null && filters.minExpectedYield !== '') {
        params.min_expected_yield = String(filters.minExpectedYield);
      }
      // #781 (INIT-description-ner): encode the three description-NER
      // booleans. Only "on" persists to the URL — the absence of a key
      // is the "no filter" default so we don't carry `?...=0` for every
      // toggle the user hasn't enabled.
      if (filters.gatedCommunityOnly) params.gated_community_only = '1';
      if (filters.noHoaOnly) params.no_hoa_only = '1';
      if (filters.subdividableOnly) params.subdividable_only = '1';
      // #783 (INIT-photo-content): emit the three photo-content
      // booleans. Only "on" persists to the URL — absence is the
      // "no filter" default.
      if (filters.hasHousePhotoOnly) params.has_house_photo_only = '1';
      if (filters.hasWaterPhotoOnly) params.has_water_photo_only = '1';
      if (filters.hasLongViewPhotoOnly) params.has_long_view_photo_only = '1';
      // OPP-3 stable-ground filter (REQ-005): only encode when active so
      // the URL stays clean for the majority of sessions without the filter.
      if (filters.stableGroundOnly) params.stable_ground = '1';
      // #997 alt-energy generation-site proximity + tech. URL keys match
      // the backend `/parcels` query params so a shared link applies the
      // same cap + tech restriction on the recipient's view. Only encode
      // when a distance is set (the tech alone is meaningless without a
      // proximity cap, mirroring the backend which requires the distance).
      if (filters.maxGenerationDist != null && filters.maxGenerationDist !== '') {
        params.max_dist_generation_km = String(filters.maxGenerationDist);
        if (filters.generationTech) params.generation_tech = filters.generationTech;
      }
      // #991 transmission-line proximity. URL keys: `max_powerline_dist_km`
      // (km-valued, distinct from the metres-valued backend param to avoid
      // unit ambiguity in shared links), plus voltage/class/type. Each is
      // encoded independently — the voltage/class/type filters stand alone
      // without a distance cap (backend uses an 8 km default radius).
      if (filters.maxPowerlineDist != null && filters.maxPowerlineDist !== '') {
        params.max_powerline_dist_km = String(filters.maxPowerlineDist);
      }
      if (filters.minPowerlineVoltage != null && filters.minPowerlineVoltage !== '') {
        params.min_powerline_voltage = String(filters.minPowerlineVoltage);
      }
      if (filters.powerlineVoltClass) params.powerline_volt_class = filters.powerlineVoltClass;
      if (filters.powerlineLineType) params.powerline_line_type = filters.powerlineLineType;
      // Sync the shareable query string to the address bar WITHOUT routing
      // through react-router. Under a reverse-proxy sub-path
      // (orbital-laser.com/land-ai) the router basename is "/land-ai", and
      // routing this debounced sync through `setSearchParams` there re-applied
      // the basename on every write, emitting "/land-ai/land-ai?c=..." — a path
      // that strips to "/land-ai", matches only the "*" catch-all, and
      // redirects back to "/", so the effect re-fired forever (the idle
      // homepage flicker the owner reported). We only need the URL bar to
      // reflect map state for bookmarking, not a router re-render, so write it
      // with a raw replaceState against the CURRENT pathname: no basename math,
      // so we never leave SearchPage's route and the catch-all loop can't
      // start. The equality guard makes the write idempotent (no history churn
      // when nothing changed). Mirrors the raw replaceState already used for the
      // hash sync in ParcelDetail.js. A root deploy has pathname "/" so this
      // writes "/?c=..." exactly as before. Reads still work: react-router
      // parses window.location.search into `searchParams` on the next real
      // navigation / page load, so bookmarked URLs hydrate normally.
      const search = new URLSearchParams(params).toString();
      const nextUrl = search
        ? `${window.location.pathname}?${search}`
        : window.location.pathname;
      if (nextUrl !== window.location.pathname + window.location.search) {
        window.history.replaceState(window.history.state, '', nextUrl);
      }
    }, 600);
    return () => { if (urlSyncTimer.current) clearTimeout(urlSyncTimer.current); };
  }, [mapView, filters.state, filters.minArea, filters.maxArea, filters.minPrice, filters.maxPrice, filters.thesisTag, filters.maxClimateRisk, filters.minListingQuality, filters.minSaleProbability60d, filters.maxPctAboveFairValue, filters.minExpectedYield, filters.gatedCommunityOnly, filters.noHoaOnly, filters.subdividableOnly, filters.hasHousePhotoOnly, filters.hasWaterPhotoOnly, filters.hasLongViewPhotoOnly, filters.stableGroundOnly, filters.maxGenerationDist, filters.generationTech, filters.maxPowerlineDist, filters.minPowerlineVoltage, filters.powerlineVoltClass, filters.powerlineLineType, viewMode, listSort]);

  const handleMapData = useCallback((data) => {
    setMapData(data);
    setLoading(false);
  }, []);

  const handleViewChange = useCallback((center, zoom) => {
    setMapView({ center, zoom });
  }, []);

  const handleBoundsChange = useCallback((bboxStr) => {
    setBbox(bboxStr);
  }, []);

  // PR-3: marker-tap handler. On phone, instead of letting MapView's popup
  // open (which overflows a 390pt viewport), we capture the parcel and
  // open the bottom ParcelSummarySheet. On tablet/desktop this stays
  // wired to setSelectedParcel as before so the popup behaviour is intact.
  const handleSelectParcel = useCallback(
    (point) => {
      if (isPhone) {
        setSummaryParcel(point);
      } else {
        setSelectedParcel(point);
      }
    },
    [isPhone],
  );

  // PR-3: bookmark toggle for the summary sheet. Optimistically updates the
  // bookmarkedIds set so the Save → Saved button flip is immediate; the
  // API call resolves in the background. Dispatches the same
  // 'bookmarks:changed' window event SavedParcels listens for so the
  // BottomTabBar badge stays in sync.
  const toggleSummaryBookmark = useCallback(
    async (parcelId) => {
      if (!parcelId || summarySaving) return;
      const wasBookmarked = bookmarkedIds.has(parcelId);
      // Optimistic toggle.
      setBookmarkedIds((prev) => {
        const next = new Set(prev);
        if (wasBookmarked) next.delete(parcelId);
        else next.add(parcelId);
        return next;
      });
      setSummarySaving(true);
      try {
        if (wasBookmarked) await deleteBookmark(parcelId);
        else await createBookmark(parcelId);
        try {
          window.dispatchEvent(new Event('bookmarks:changed'));
        } catch { /* ignore */ }
      } catch (err) {
        // Roll back the optimistic update so the UI matches server state.
        setBookmarkedIds((prev) => {
          const next = new Set(prev);
          if (wasBookmarked) next.add(parcelId);
          else next.delete(parcelId);
          return next;
        });
        console.error('Bookmark toggle failed:', err);
      } finally {
        setSummarySaving(false);
      }
    },
    [bookmarkedIds, summarySaving],
  );

  // PR-3 phone branch: skip the legacy "Show/Hide Filters" button + sidebar
  // FilterPanel entirely; the pill strip + Filters button + FilterSheet
  // replace them.
  const activeCount = countActiveFilters(filters);

  return (
    <div className={`search-page ${isPhone ? 'search-page--phone' : ''}`}>
      {/* Tablet (768-1024): keep the legacy "Show Filters" toggle button
          so the sidebar can collapse when the user wants more map.
          Desktop: button is hidden by .mobile-filter-toggle's CSS.
          Phone (≤767): suppressed entirely — replaced by the Filters
          button rendered above the map. */}
      {isMobile && !isPhone && (
        <button
          className="mobile-filter-toggle"
          onClick={() => setFiltersOpen(!filtersOpen)}
        >
          {filtersOpen ? 'Hide Filters' : 'Show Filters'}
        </button>
      )}
      {/* Sidebar FilterPanel — only rendered on tablet+desktop. On phone
          the FilterSheet (full-screen modal) owns the FilterPanel.
          Tablet (768-1024) wraps the same FilterPanel in PR-5's
          accordion sections to match the phone sheet's 4-group layout.
          Desktop (≥1025, PR-6) reuses the same accordion so the three
          breakpoints share one mental model; section open/closed
          state persists in localStorage. */}
      {!isPhone && (
        <div className={`filter-panel-wrapper ${isMobile && !filtersOpen ? 'filter-panel-wrapper--hidden' : ''}`}>
          {useAccordionPanel ? (
            <FilterPanelAccordion
              filters={filters}
              setFilters={setFilters}
            />
          ) : (
            <FilterPanel
              filters={filters}
              setFilters={setFilters}
            />
          )}
        </div>
      )}
      <div
        className="map-and-list-wrapper"
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: isPhone ? 'column' : 'row',
          minWidth: 0,
          position: 'relative',
        }}
      >
      {/* Phone-only: pill strip + Filters button row above the map.
          Renders even when no filters are active so the Filters
          button stays in a stable position. */}
      {isPhone && (
        <div
          className="phone-filter-bar"
          data-testid="phone-filter-bar"
          style={{
            display: 'flex',
            flexDirection: 'column',
            background: '#1a1a2e',
            flexShrink: 0,
          }}
        >
          <FilterPillStrip filters={filters} setFilters={setFilters} />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 12px',
              borderBottom: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <button
              type="button"
              className="phone-filters-button"
              data-testid="phone-filters-button"
              onClick={() => setFilterSheetOpen(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 14px',
                background: '#e94560',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                minHeight: 40,
                cursor: 'pointer',
              }}
            >
              Filters
              {activeCount > 0 && (
                <span
                  className="phone-filters-badge"
                  data-testid="phone-filters-badge"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 22,
                    height: 22,
                    padding: '0 6px',
                    borderRadius: 11,
                    background: '#fff',
                    color: '#e94560',
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  {activeCount > 99 ? '99+' : activeCount}
                </span>
              )}
            </button>
          </div>
        </div>
      )}
      <div
        className="map-wrapper"
        style={{
          // Hide the map entirely in list-only mode; constrain to 60%
          // in split mode; full width otherwise. Mobile collapses to
          // map-only regardless because the panel needs ≥360px to be
          // useful (#146 slice 1).
          flex: viewMode === 'list' ? '0 0 0' : 1,
          display: viewMode === 'list' ? 'none' : 'flex',
          flexDirection: 'column',
          position: 'relative',
          minWidth: 0,
        }}
      >
        {/* Result count bar */}
        <div className="results-bar">
          <span className="result-count">
            {(() => {
              // "My bookmarks only" is a client-side filter over the
              // already-fetched points — MapView renders only the
              // subset that matches `bookmarkedIds`, so the backend
              // count (mapData.count) would be misleading when that
              // toggle is on. Compute the displayed count from the
              // filtered set instead.
              const showingBookmarks = filters.bookmarksOnly && bookmarkedIds.size > 0;
              // Apply the same hide-by-default rule the map uses
              // (#124) when computing the displayed count, so the
              // "Showing X parcels" badge matches what the user
              // actually sees on the map.
              const showHidden = !!filters.showHidden;
              const points = mapData.points || [];
              const afterBookmark = showingBookmarks
                ? points.filter(p => bookmarkedIds.has(p.parcel_id))
                : points;
              const afterHide = showHidden
                ? afterBookmark
                : afterBookmark.filter(p => !downvotedIds.has(p.parcel_id));
              const afterViewed = filters.notViewed && viewedIds.size > 0
                ? afterHide.filter(p => !viewedIds.has(p.parcel_id))
                : afterHide;
              // Use the filtered point-list length whenever a client-
              // side filter is actually changing the visible set
              // (bookmarks-only, downvote-hide, or not-viewed). Otherwise
              // prefer mapData.total — the unbounded COUNT(*) added in
              // #540 that matches /parcels/table's `total`, so the map
              // header and the list header report the SAME number.
              // Fall back to mapData.count (the capped point-array
              // length) when an older client/server is missing total.
              const hidingHasEffect = !showHidden && downvotedIds.size > 0;
              const notViewedHasEffect = !!filters.notViewed && viewedIds.size > 0;
              const useFiltered = showingBookmarks || hidingHasEffect || notViewedHasEffect;
              const headlineTotal = mapData.total ?? mapData.count;
              const visible = useFiltered ? afterViewed.length : headlineTotal;
              // Truncation caveat: when total > count, the map
              // dropped some markers under the per-request cap.
              // Show "(showing X markers of Y — zoom in to see all)"
              // instead of the older "limit reached" message.
              const truncated = !useFiltered
                && mapData.total != null
                && mapData.count < mapData.total;
              return (
                <>
                  Showing <strong>{visible.toLocaleString()}</strong> parcel{visible !== 1 ? 's' : ''} in view
                  {showingBookmarks && (
                    <span style={{ color: '#6b7280', marginLeft: '0.5rem' }}>
                      (bookmarks only)
                    </span>
                  )}
                  {!showingBookmarks && truncated && (
                    <span style={{ color: '#e67e22', marginLeft: '0.5rem' }}>
                      (showing {mapData.count.toLocaleString()} markers — zoom in to see all)
                    </span>
                  )}
                  {!showingBookmarks && !truncated && mapData.total == null && mapData.count >= mapData.limit && (
                    <span style={{ color: '#e67e22', marginLeft: '0.5rem' }}>
                      (limit reached — zoom in for more)
                    </span>
                  )}
                  {showHidden && downvotedIds.size > 0 && (
                    <span style={{ color: '#dc2626', marginLeft: '0.5rem' }}>
                      (showing hidden in red)
                    </span>
                  )}
                  {filters.state ? ` | State: ${filters.state}` : ''}
                  {filters.q ? ` | Search: "${filters.q}"` : ''}
                </>
              );
            })()}
          </span>
        </div>
        <MapView
          filters={filters}
          selectedParcel={selectedParcel}
          onSelectParcel={handleSelectParcel}
          onMapData={handleMapData}
          initialCenter={mapView.center}
          initialZoom={mapView.zoom}
          onViewChange={handleViewChange}
          onBoundsChange={handleBoundsChange}
          bookmarkedIds={bookmarkedIds}
          downvotedIds={downvotedIds}
          focusedParcel={focusedParcel}
          hoveredParcelId={hoveredParcelId}
          viewedIds={viewedIds}
        />
      </div>
      {/* View-mode toggle floats over the map in the top-left of the
          map area. Mobile gets a 2-option toggle (Map / List) since
          Split needs ≥360px for the panel which doesn't fit on phones
          alongside the map; desktop gets all three.
          `left: 62` sits it to the RIGHT of Leaflet's native zoom control
          (top-left corner, ~46px wide including the 44px mobile touch
          targets) so the toggle no longer covers the +/- buttons (#1037).
          `top: 56` clears the results-bar rendered above the map. */}
      <div
        className="view-mode-toggle"
        style={{
          position: 'absolute',
          top: 56, left: 62, zIndex: 1000,
          background: '#fff',
          border: '1px solid #ccc', borderRadius: 4,
          display: 'flex', overflow: 'hidden',
          boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
          fontSize: '0.78rem',
        }}
        role="group"
        aria-label="View mode"
      >
        {(isMobile
          ? [{ v: 'map', label: 'Map' }, { v: 'list', label: 'List' }]
          : [
              { v: 'map',   label: 'Map' },
              { v: 'split', label: 'Split' },
              { v: 'list',  label: 'List' },
            ]
        ).map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => setViewMode(opt.v)}
              aria-pressed={viewMode === opt.v}
              style={{
                padding: '4px 10px',
                minHeight: 44,
                border: 'none',
                background: viewMode === opt.v ? '#1976d2' : 'transparent',
                color: viewMode === opt.v ? '#fff' : '#333',
                cursor: 'pointer',
                fontWeight: viewMode === opt.v ? 600 : 400,
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      {/* Right-side list panel. On desktop it shares the row with
          the map in split mode and takes the rest of the row in list
          mode. On mobile (where Split isn't offered) it takes the
          full row width when the user picks List. */}
      {(viewMode === 'split' || viewMode === 'list') && (
        <div
          className="map-list-panel-wrapper"
          style={{
            flex: 1,
            // Desktop split: cap at 40% of the row so the map keeps
            // most of the canvas. Desktop list / mobile list: no cap.
            ...(viewMode === 'split' && !isMobile && {
              flex: '0 0 40%', minWidth: 360, maxWidth: 720,
            }),
            // Mobile gets no min/max — panel takes whatever the row gives.
            ...(!isMobile && viewMode === 'list' && { minWidth: 360 }),
            display: 'flex',
          }}
        >
          <MapListPanel
            bbox={bbox}
            stateFilter={filters.state}
            isListed={filters.isListed ? true : filters.offMarketOnly ? false : null}
            minAcres={filters.minArea ? parseFloat(filters.minArea) : undefined}
            maxAcres={filters.maxArea ? parseFloat(filters.maxArea) : undefined}
            minPrice={filters.minPrice ? parseFloat(filters.minPrice) : undefined}
            maxPrice={filters.maxPrice ? parseFloat(filters.maxPrice) : undefined}
            // Full server-side parity with /parcels/points (#194). Map
            // and list now apply the same filter set; toggling any
            // FilterPanel checkbox / range produces identical results.
            minPricePerAcre={filters.minPricePerAcre ? parseFloat(filters.minPricePerAcre) : undefined}
            maxPricePerAcre={filters.maxPricePerAcre ? parseFloat(filters.maxPricePerAcre) : undefined}
            maxWildfireRisk={filters.maxWildfireRisk != null ? parseFloat(filters.maxWildfireRisk) : undefined}
            maxFloodRisk={filters.maxFloodRisk != null ? parseFloat(filters.maxFloodRisk) : undefined}
            // Days-on-market parity with MapView (#1063) — see deriveDomBounds.
            minDom={deriveDomBounds(filters).minDom}
            maxDom={deriveDomBounds(filters).maxDom}
            minElevationChange={filters.minElevationChange ? parseFloat(filters.minElevationChange) : undefined}
            maxElevationChange={filters.maxElevationChange ? parseFloat(filters.maxElevationChange) : undefined}
            searchQuery={filters.q || undefined}
            maxMineDist={filters.maxMineDist != null ? parseFloat(filters.maxMineDist) : undefined}
            minMinesNearby={filters.minMinesNearby != null ? parseInt(filters.minMinesNearby, 10) : undefined}
            // #997 alt-energy generation-site proximity + tech.
            maxGenerationDist={filters.maxGenerationDist ? parseFloat(filters.maxGenerationDist) : undefined}
            generationTech={filters.generationTech || undefined}
            // #991 HIFLD transmission-line proximity (distance km + voltage/class/type).
            maxPowerlineDist={filters.maxPowerlineDist ? parseFloat(filters.maxPowerlineDist) : undefined}
            minPowerlineVoltage={filters.minPowerlineVoltage ? parseFloat(filters.minPowerlineVoltage) : undefined}
            powerlineVoltClass={filters.powerlineVoltClass || undefined}
            powerlineLineType={filters.powerlineLineType || undefined}
            priceChangedRecently={!!filters.priceChangedRecently}
            priceChangeDays={filters.priceChangeDays != null ? parseInt(filters.priceChangeDays, 10) : undefined}
            minGoldProb={filters.minGoldProb != null ? parseFloat(filters.minGoldProb) : undefined}
            minCopperProb={filters.minCopperProb != null ? parseFloat(filters.minCopperProb) : undefined}
            minSilverProb={filters.minSilverProb != null ? parseFloat(filters.minSilverProb) : undefined}
            minUraniumProb={filters.minUraniumProb != null ? parseFloat(filters.minUraniumProb) : undefined}
            minLithiumProb={filters.minLithiumProb != null ? parseFloat(filters.minLithiumProb) : undefined}
            minReeProb={filters.minReeProb != null ? parseFloat(filters.minReeProb) : undefined}
            minNiobiumProb={filters.minNiobiumProb != null ? parseFloat(filters.minNiobiumProb) : undefined}
            excludeLiens={!!filters.excludeLiens}
            excludeEasements={!!filters.excludeEasements}
            distressedOnly={!!filters.distressedOnly}
            landOnlyConfirmed={!!filters.landOnlyConfirmed}
            excludeLandlocked={!!filters.excludeLandlocked}
            // #781 description-NER toggles -- forward as booleans so MapListPanel
            // emits the matching gated_community_only / no_hoa_only /
            // subdividable_only API params alongside the map's /parcels/points
            // call. Keeping map + list parity is what avoids the count-mismatch
            // bug #194 surfaced for the original tri-state filters.
            gatedCommunityOnly={!!filters.gatedCommunityOnly}
            noHoaOnly={!!filters.noHoaOnly}
            subdividableOnly={!!filters.subdividableOnly}
            // #783 (INIT-photo-content): forward the three photo-content
            // toggles so MapListPanel emits the matching has_*_photo_only
            // params and the map + list stay in lockstep.
            hasHousePhotoOnly={!!filters.hasHousePhotoOnly}
            hasWaterPhotoOnly={!!filters.hasWaterPhotoOnly}
            hasLongViewPhotoOnly={!!filters.hasLongViewPhotoOnly}
            minListingQuality={filters.minListingQuality != null && filters.minListingQuality !== '' ? Number(filters.minListingQuality) : undefined}
            minSaleProbability60d={filters.minSaleProbability60d != null && filters.minSaleProbability60d !== '' ? Number(filters.minSaleProbability60d) / 100 : undefined}
            minExpectedYield={filters.minExpectedYield != null && filters.minExpectedYield !== '' ? Number(filters.minExpectedYield) : undefined}
            onParcelHover={setHoveredParcelId}
            onFocusOnMap={viewMode === 'split' ? setFocusedParcel : undefined}
            listSort={listSort}
            onSortChange={setListSort}
            viewedIds={viewedIds}
            notViewed={filters.notViewed}
            bookmarkedIds={bookmarkedIds}
            downvotedIds={downvotedIds}
            bookmarksOnly={filters.bookmarksOnly}
            showHidden={!!filters.showHidden}
            thesisTag={filters.thesisTag || undefined}
          />
        </div>
      )}
      </div>
      {/* PR-3 phone sheets — rendered always but only visible on phone via
          their root @media gate. Mounting unconditionally keeps the
          state transitions smooth if the user resizes across the 767
          boundary; tablet/desktop pay only the cost of an empty
          aria-hidden root div. */}
      {isPhone && (
        <FilterSheet
          open={filterSheetOpen}
          onClose={() => setFilterSheetOpen(false)}
          filters={filters}
          setFilters={setFilters}
        />
      )}
      {isPhone && (
        <ParcelSummarySheet
          parcel={summaryParcel}
          onClose={() => setSummaryParcel(null)}
          isBookmarked={summaryParcel ? bookmarkedIds.has(summaryParcel.parcel_id) : false}
          onToggleBookmark={toggleSummaryBookmark}
          isSaving={summarySaving}
        />
      )}
    </div>
  );
}

export default SearchPage;
